import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/api';
import { clearCachedStations } from '../lib/offline';
import { unsubscribeFromPush } from '../lib/push';

const AuthContext = createContext(null);

const clearApiCaches = async () => {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter(k => k.startsWith('api-')).map(k => caches.delete(k)));
    }
  } catch (e) { console.warn('clearApiCaches failed', e); }
  await clearCachedStations();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Session lives in an httpOnly cookie (not readable from JS) — always probe /auth/me.
    api.get('/auth/me')
      .then(r => setUser(r.data.user))
      .catch(() => {
        localStorage.removeItem('pc_token'); localStorage.removeItem('pc_user');
        // No session on this device: kill any leftover push subscription so
        // logged-out devices stop receiving notifications just by opening the app.
        unsubscribeFromPush().catch(() => {});
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      // Always clear cross-user API cache before authenticating
      await clearApiCaches();
      const r = await api.post('/auth/login', { email, password });
      // Token now travels in an httpOnly cookie — never stored in localStorage
      localStorage.removeItem('pc_token');
      localStorage.removeItem('pc_user');
      setUser(r.data.user);
      return { success: true, user: r.data.user };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Error al iniciar sesión' };
    }
  }, []);

  const register = useCallback(async (form) => {
    try {
      const r = await api.post('/auth/register', form);
      // Backend returns { pending: true } for new self-registrations.
      // Do NOT auto-login: the user must wait for admin approval.
      if (r.data && r.data.pending) {
        return { success: true, pending: true, user: r.data.user, message: r.data.message };
      }
      // Legacy / future direct-activation path (cookie set by backend)
      if (r.data?.user && !r.data.pending) {
        setUser(r.data.user);
      }
      return { success: true, user: r.data.user };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Error al registrar' };
    }
  }, []);

  const logout = useCallback(async () => {
    // Remove this device's push subscription BEFORE clearing the session
    // (the unsubscribe endpoint needs the auth cookie). 3s safety timeout.
    try { await Promise.race([unsubscribeFromPush(), new Promise(res => setTimeout(res, 3000))]); } catch (e) { /* best-effort */ }
    try { await api.post('/auth/logout'); } catch (e) { /* cookie clear is best-effort */ }
    setUser(null);
    localStorage.removeItem('pc_token');
    localStorage.removeItem('pc_user');
    await clearApiCaches();
  }, []);

  // Memoize the context value so consumers don't re-render on every parent render
  const value = useMemo(
    () => ({ user, login, register, logout, loading }),
    [user, login, register, logout, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
